import React, { useEffect, useState, useContext } from "react";
import { FiUsers, FiBook, FiYoutube } from "react-icons/fi";
import { BsFillFileEarmarkPdfFill, BsFileMusic } from "react-icons/bs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BiBible } from "react-icons/bi";

// Chat imports removed - admin panel chat disabled
// import { LuSend } from "react-icons/lu";
import {
  // chatHistory,
  getDashboardData,
  dashboardGraphData,
  // sendMessage,
  // createChatSession,
} from "../../../../libs/api/dashboardApi";
import { AppContext } from "../../../../context/appContext";

const StatCard = ({ icon: Icon, title, value, className }) => (
  <div className="bg-white p-4 sm:p-5 flex rounded-2xl shadow-md">
    <div className="flex items-start gap-4">
      {/* Icon container */}
      <div className={`p-3 rounded-full ${className} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>

      {/* Text content */}
      <div className="flex flex-col justify-center">
        <p className="text-sm sm:text-base text-gray-600 font-medium break-words">
          {title}
        </p>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">
          {value}
        </h3>
      </div>
    </div>
  </div>

);

const NotificationItem = ({ name, email, amount }) => (
  <div className="flex items-center justify-between py-3">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gray-200"></div>
      <div>
        <p className="font-semibold text-sm sm:text-base">{name}</p>
        <p className="text-xs text-gray-500">{email}</p>
      </div>
    </div>
    <p className="font-bold text-sm sm:text-base">${amount}</p>
  </div>
);

export default function Dashboard() {
  const { t, formatNum, isRTL } = useContext(AppContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Chat state removed - admin panel chat disabled
  // const [chatMessages, setChatMessages] = useState([]);
  // const [message, setMessage] = useState("");
  const [graphData, setGraphData] = useState([]);
  // const [msgLoading, setMsgLoading] = useState(false);
  // const chatContainerRef = useRef(null);

  // Chat scroll function removed - admin panel chat disabled
  // const scrollToBottom = () => {
  //   if (chatContainerRef.current) {
  //     chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  //   }
  // };

  const getDashboardDataApi = async () => {
    setLoading(true);
    const response = await getDashboardData();
    try {
      if (response.status === 200 || response.status === 201) {
        setDashboardData(response?.data?.data || {});
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getGraphDataApi = async () => {
    const response = await dashboardGraphData();
    if (response.status === 200 || response.status === 201) {
      // Map the response data to match the chart's expected format
      const mappedData =
        response?.data?.data?.map((item) => ({
          month: item.month,
          value: item.visitCount,
        })) || [];
      setGraphData(mappedData);
    }
  };

  // Chat functions removed - admin panel chat disabled
  // const sendMessageApi = async (messageText) => { ... };
  // const getChatHistoryApi = async () => { ... };
  // const handleSendMessage = () => { ... };
  // const handleKeyPress = (e) => { ... };

  useEffect(() => {
    getDashboardDataApi();
    getGraphDataApi();
    // Chat API call removed - admin panel chat disabled
    // getChatHistoryApi();
  }, []);

  // Chat scroll useEffect removed - admin panel chat disabled
  // useEffect(() => {
  //   scrollToBottom();
  // }, [chatMessages, msgLoading]);

  return (
    <div>
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="p-4 sm:p-6 sm:py-4 max-w-full">
          <h1 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6">
            {t('dashboard.title')}
          </h1>
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
            {/* First Column: Count and AI Chat */}
            <div className="flex flex-col gap-4 sm:gap-6 h-full justify-between xl:col-span-2">
              <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 space-y-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <StatCard
                  icon={FiUsers}
                  title={t('dashboard.activeSession')}
                  value={formatNum(dashboardData?.totalSessions || 0)}
                  className="bg-blue-500"
                />
                <StatCard
                  icon={BiBible}
                  title={t('dashboard.totalBible')}
                  value={formatNum(dashboardData?.totalBibles || 0)}
                  className="bg-blue-500"
                />
                <StatCard
                  icon={BsFillFileEarmarkPdfFill}
                  title={t('dashboard.uploadedPDFs')}
                  value={formatNum(dashboardData?.totalPDFs || 0)}
                  className="bg-blue-500"
                />
                <StatCard
                  icon={BsFileMusic}
                  title={t('dashboard.uploadedAudio')}
                  value={formatNum(dashboardData?.totalMP3s || 0)}
                  className="bg-blue-500"
                />
                <StatCard
                  icon={FiBook}
                  title={t('dashboard.books')}
                  value={formatNum(dashboardData?.totalBooks || 0)}
                  className="bg-blue-500"
                />
                <StatCard
                  icon={FiYoutube}
                  title={t('dashboard.youtubeLinks')}
                  value={formatNum(dashboardData?.totalYouTubeLinks || 0)}
                  className="bg-blue-500"
                />
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-md">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-2">
                  <h2 className="text-lg sm:text-xl font-bold">
                    {t('dashboard.userAnalytics')}
                  </h2>
                </div>
                <div className="w-full h-[250px] sm:h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={graphData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#3b82f6"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

      {/* Chat UI removed - admin panel chat disabled */}
      {/* <div className="flex flex-col gap-4 sm:gap-6 h-full">...</div> */}

          </div>
        </div>
      )}
    </div>
  );
}
